import { useState, useEffect } from 'react';
import { ConfigProvider, useConfig } from './context/ConfigContext';
import AgentInfoPanel from './components/AgentInfoPanel';
import ApiBasicConfig from './components/ApiBasicConfig';
import PromptTemplateConfig from './components/PromptTemplateConfig';
import InputParamsConfig from './components/InputParamsConfig';
import ChatInterface from './components/ChatInterface';
import CapabilityRegistry from './components/CapabilityRegistry';
import SuperAgentConsole from './components/SuperAgentConsole';
import OperationsDashboard from './components/OperationsDashboard';
import DiagnosticTool from './components/DiagnosticTool';
import InitWizard from './components/InitWizard';
import SetupLauncher from './components/SetupLauncher';
import ModelManagement from './components/ModelManagement';
import KnowledgeBase from './components/KnowledgeBase';
import AiGuideAssistant from './components/AiGuideAssistant';
import { Save, Send, AlertCircle, CheckCircle, Database, BarChart2, MessageSquare, Settings, Bot, Stethoscope, Rocket, Cpu, Library } from 'lucide-react';

function ValidationErrors({ errors }) {
  if (errors.length === 0) return null;
  return (
    <div className="fixed top-4 right-4 z-50 bg-red-50 border border-red-200 rounded-lg shadow-lg p-4 max-w-md">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-sm font-medium text-red-800">请完善以下信息：</h4>
          <ul className="mt-2 text-xs text-red-700 space-y-1">
            {errors.map((error, index) => <li key={index}>• {error}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SuccessToast({ message, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg shadow-lg p-4">
      <div className="flex items-center gap-3">
        <CheckCircle className="w-5 h-5 text-green-500" />
        <p className="text-sm font-medium text-green-800">{message}</p>
      </div>
    </div>
  );
}

const NAV_TABS = [
  { key: 'wizard', label: '部署清单（设置）', icon: Rocket, badge: '三期 P0', desc: '完整初始化清单；常驻入口为全局右上角 Setup 进度 pill，完成后隐退到此' },
  { key: 'models', label: '模型管理', icon: Cpu, desc: '二期：四类模型治理 + 主从切换熔断自愈' },
  { key: 'knowledge', label: '知识库管理', icon: Library, desc: '二期：知识库 + 5步创建向导（向量库部署时已配）' },
  { key: 'config', label: '智能体配置', icon: Settings, desc: '二期：API接入 + 自定义业务参数' },
  { key: 'registry', label: '能力注册中心', icon: Database, badge: '三期 P0', desc: '接口网关：API / MCP / Skill 统一目录，强制代理可观测' },
  { key: 'superagent', label: '超级智能体', icon: Bot, badge: '三期 P0', desc: '工具调用引擎：会路由会调工具的平台第一方大脑' },
  { key: 'dashboard', label: '运营看板', icon: BarChart2, badge: '三期 P0', desc: '可观测指标 + 工具调用量 · 不含外部Agent内部Token' },
  { key: 'diagnostic', label: '诊断工具', icon: Stethoscope, badge: '三期 P1', desc: '批量跑分 / 单次调试 / 回归对比（质量评测）' },
  { key: 'chat', label: '会话界面', icon: MessageSquare, desc: '上下文感知 + 内容块渲染' },
];

function AppContent() {
  const { config } = useConfig();
  const [activeTab, setActiveTab] = useState('config');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isApiBasicConfigCollapsed, setIsApiBasicConfigCollapsed] = useState(false);

  const validateForm = () => {
    const errors = [];
    if (!config.agentInfo.name.trim()) errors.push('智能体名称不能为空');
    if (!config.apiConfig.apiName.trim()) errors.push('API 名称不能为空');
    if (!config.apiConfig.apiUrl.trim()) errors.push('API 地址不能为空');
    if (config.apiConfig.authType === 'API_KEY' && !config.apiConfig.apiKey.trim()) errors.push('API_KEY 不能为空');
    if (config.apiConfig.authType === 'BEARER_TOKEN' && !config.apiConfig.bearerToken.trim()) errors.push('Bearer Token 不能为空');
    return errors;
  };

  const handleSave = () => {
    const errors = validateForm();
    if (errors.length > 0) { setValidationErrors(errors); setShowValidationErrors(true); setTimeout(() => setShowValidationErrors(false), 5000); return; }
    setSuccessMessage('配置已保存成功！'); setShowSuccess(true);
  };

  const handleTest = () => {
    const errors = validateForm();
    if (errors.length > 0) { setValidationErrors(errors); setShowValidationErrors(true); setTimeout(() => setShowValidationErrors(false), 5000); return; }
    setSuccessMessage('开始接口测试...'); setShowSuccess(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {showValidationErrors && <ValidationErrors errors={validationErrors} />}
      {showSuccess && <SuccessToast message={successMessage} onClose={() => setShowSuccess(false)} />}

      {/* 左侧导航 */}
      <aside className="w-56 bg-white border-r border-gray-200 flex flex-col fixed inset-y-0 left-0 z-10">
        <div className="flex items-center gap-2 px-5 h-14 border-b border-gray-100 flex-shrink-0">
          <div className="w-7 h-7 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-bold">E</span>
          </div>
          <span className="text-base font-bold text-gray-900">ECHO</span>
          <span className="text-xs text-gray-400 font-normal">智能体平台</span>
        </div>
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {NAV_TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                title={tab.desc}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 text-left truncate">{tab.label}</span>
                {tab.badge && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                    active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                  }`}>{tab.badge}</span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* 右侧内容区 */}
      <div className="flex-1 ml-56 min-w-0">
      {/* 页面内容 */}
      {activeTab === 'config' && (
        <main className="w-full max-w-[1440px] mx-auto px-6 py-6 relative">
          <div className="space-y-6">
            <AgentInfoPanel />
            <div id="api-basic-config" className="card-light p-4">
              <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={() => setIsApiBasicConfigCollapsed(!isApiBasicConfigCollapsed)}>
                <div className="section-title mb-0">API 基本信息配置</div>
                <button className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points={isApiBasicConfigCollapsed ? "6 9 12 15 18 9" : "6 15 12 9 18 15"} />
                  </svg>
                </button>
              </div>
              {!isApiBasicConfigCollapsed && <ApiBasicConfig />}
            </div>
            <PromptTemplateConfig />
            <InputParamsConfig />
            <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
              <button onClick={handleTest} className="btn-secondary flex items-center gap-2">
                <Send className="w-4 h-4" /> 接口测试
              </button>
              <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                <Save className="w-4 h-4" /> 保存配置
              </button>
            </div>
          </div>
          {/* AI 引导助手（三期新增：AI引导式配置，监听表单状态实时提示） */}
          <AiGuideAssistant />
        </main>
      )}

      {activeTab === 'wizard' && <InitWizard onNavigate={setActiveTab} />}
      {activeTab === 'models' && <ModelManagement />}
      {activeTab === 'knowledge' && <KnowledgeBase />}
      {activeTab === 'registry' && <CapabilityRegistry />}
      {activeTab === 'superagent' && <SuperAgentConsole />}
      {activeTab === 'dashboard' && <OperationsDashboard />}
      {activeTab === 'diagnostic' && <DiagnosticTool />}
      {activeTab === 'chat' && <ChatInterface />}
      </div>

      {/* 全局右上角 Setup 入口（适配"无独立首页"：常驻、完成后隐退到设置） */}
      <SetupLauncher onNavigate={setActiveTab} />
    </div>
  );
}

export default function App() {
  return (
    <ConfigProvider>
      <AppContent />
    </ConfigProvider>
  );
}
